/**
 * Country Helpers Utility
 * Helper functions for country data manipulation
 */

/**
 * Get list of country codes that have members
 * @param {Array} members - Array of member objects
 * @returns {Array<string>} Array of unique country codes
 */
export const getCountriesWithMembers = (members) => {
  if (!members || !Array.isArray(members)) {
    return [];
  }
  
  const countryCodes = members
    .map(member => member.countryCode)
    .filter(code => code); // Remove null/undefined values
  
  // Return unique country codes
  return [...new Set(countryCodes)];
};

/**
 * Get all members from a specific country
 * @param {Array} members - Array of member objects
 * @param {string} countryCode - ISO 3-letter country code
 * @returns {Array} Array of members from the specified country
 */
export const getMembersByCountry = (members, countryCode) => {
  if (!members || !Array.isArray(members) || !countryCode) {
    return [];
  }
  
  return members.filter(member => member.countryCode === countryCode);
};

/**
 * Get country name from country code
 * @param {string} countryCode - ISO 3-letter country code
 * @returns {string} Country display name
 */
export const formatCountryName = (countryCode) => {
  const countryNames = {
    'ITA': 'Italy',
    'LAO': 'Lao PDR',
    'MNG': 'Mongolia',
    'JPN': 'Japan',
    'MYS': 'Malaysia',
    // Add more as needed
  };
  
  return countryNames[countryCode] || countryCode;
};

/**
 * Check if a country has members
 * @param {Array} members - Array of member objects
 * @param {string} countryCode - ISO 3-letter country code
 * @returns {boolean} True if country has members
 */
export const hasMembers = (members, countryCode) => {
  if (!members || !Array.isArray(members) || !countryCode) {
    return false;
  }
  
  return members.some(member => member.countryCode === countryCode);
};

/**
 * Get member count for a specific country
 * @param {Array} members - Array of member objects
 * @param {string} countryCode - ISO 3-letter country code
 * @returns {number} Number of members from the country
 */
export const getMemberCount = (members, countryCode) => {
  return getMembersByCountry(members, countryCode).length;
};

/**
 * Calculate bounding box for a set of geographies
 * @param {Array} geographies - Array of geography objects
 * @param {Array<string>} highlightedIds - Array of country IDs to calculate bounds for
 * @returns {Object} Bounding box {minLat, maxLat, minLon, maxLon} or null if no valid geographies
 */
export const calculateGeoBounds = (geographies, highlightedIds) => {
  if (!geographies || !highlightedIds || highlightedIds.length === 0) {
    return null;
  }

  const highlightedGeographies = geographies.filter(geo => 
    highlightedIds.includes(geo.id)
  );

  if (highlightedGeographies.length === 0) {
    return null;
  }

  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  highlightedGeographies.forEach(geo => {
    const coordinates = geo.geometry.coordinates;
    
    // Handle different geometry types (Polygon, MultiPolygon)
    const processCoordinates = (coords) => {
      if (typeof coords[0] === 'number') {
        // This is a [lon, lat] pair
        const [lon, lat] = coords;
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
      } else {
        // This is an array of coordinates, recurse
        coords.forEach(processCoordinates);
      }
    };

    processCoordinates(coordinates);
  });

  return { minLat, maxLat, minLon, maxLon };
};

/**
 * Calculate optimal center and zoom level to fit a bounding box
 * @param {Object} bounds - Bounding box {minLat, maxLat, minLon, maxLon}
 * @param {number} containerWidth - Width of map container in pixels
 * @param {number} containerHeight - Height of map container in pixels
 * @param {number} padding - Padding factor (default 0.2 = 20% padding)
 * @returns {Object} {center: [lon, lat], zoom: number}
 */
export const calculateCenterAndZoom = (
  bounds, 
  containerWidth = 1400, 
  containerHeight = 600,
  padding = 0.2
) => {
  if (!bounds) {
    return { center: [0, 20], zoom: 1 }; // Default fallback
  }

  const { minLat, maxLat, minLon, maxLon } = bounds;

  // Calculate center
  const centerLon = (minLon + maxLon) / 2;
  const centerLat = (minLat + maxLat) / 2;

  // Calculate dimensions
  const latDiff = maxLat - minLat;
  const lonDiff = maxLon - minLon;

  // Add padding
  const paddedLatDiff = latDiff * (1 + padding);
  const paddedLonDiff = lonDiff * (1 + padding);

  // Calculate zoom level (simplified approach)
  // The base scale is 140 for zoom level 1 in the current setup
  const baseScale = 140;
  const latZoom = containerHeight / (paddedLatDiff * baseScale / 100);
  const lonZoom = containerWidth / (paddedLonDiff * baseScale / 100);
  
  // Use the smaller zoom to ensure everything fits
  const zoom = Math.min(latZoom, lonZoom, 6); // Cap at 6x zoom
  const finalZoom = Math.max(zoom, 1); // Minimum zoom of 1

  return {
    center: [centerLon, centerLat],
    zoom: finalZoom
  };
};
